import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import useSWR from "swr";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { Eraser, ShieldCheck, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";
import { api, formatApiError } from "@/lib/api";
import { ESIGN_CONSENT_TEXT } from "@/lib/policies";

const fetcher = (url) => api.get(url).then((r) => r.data);
const field = "w-full bg-white border-2 border-[rgba(211,218,221,0.5)] rounded-xl px-4 py-2.5 text-[#0a0a0a] outline-none focus:border-[#ff5147] transition";

export default function Waiver() {
  const { data: content } = useSWR("/waiver-content", fetcher);
  const [form, setForm] = useState({ participant_name: "", participant_dob: "", date_signed: new Date().toISOString().slice(0, 10), is_minor: false, guardian_name: "", signer_name: "", email: "", agreement_accepted: false, esign_consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const sigRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const resize = () => {
      const canvas = sigRef.current?.getCanvas?.();
      if (!canvas) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const parent = canvas.parentElement;
      const w = parent.clientWidth;
      canvas.width = w * ratio;
      canvas.height = 180 * ratio;
      const ctx = canvas.getContext("2d");
      ctx.scale(ratio, ratio);
      sigRef.current.clear();
    };
    const t = setTimeout(resize, 100);
    window.addEventListener("resize", resize);
    return () => { clearTimeout(t); window.removeEventListener("resize", resize); };
  }, [done]);

  const reset = () => {
    setForm({ participant_name: "", participant_dob: "", date_signed: new Date().toISOString().slice(0, 10), is_minor: false, guardian_name: "", signer_name: "", email: "", agreement_accepted: false, esign_consent: false });
    sigRef.current?.clear();
    setDone(false);
  };

  const submit = async () => {
    if (!form.participant_name.trim()) { toast.error("Please enter the participant's name."); return; }
    if (form.is_minor && !form.guardian_name.trim()) { toast.error("Please enter the parent/guardian name."); return; }
    if (!form.signer_name.trim()) { toast.error("Please enter the signer's full name."); return; }
    if (!form.agreement_accepted) { toast.error("Please check the box to agree."); return; }
    if (!form.esign_consent) { toast.error("Please consent to signing electronically."); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error("Please sign in the box."); return; }
    setSubmitting(true);
    try {
      await api.post("/waivers", { ...form, signature_data: sigRef.current.getCanvas().toDataURL("image/png") });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not submit the waiver."); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="pt-32 pb-24 min-h-screen">
        <Seo title="Waiver Signed | Keys Event Center" />
        <div className="mx-auto max-w-lg px-6 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="card p-10">
            <CheckCircle2 className="text-[#21e6cf] mx-auto mb-4" size={56} />
            <h1 className="font-display text-3xl font-bold text-[#f5f0ff] mb-2">Waiver Signed</h1>
            <p className="text-[#9aa0c0] mb-8">Thanks, {form.signer_name}! The signed waiver for <span className="text-[#f5f0ff] font-semibold">{form.participant_name}</span> is on file. Enjoy your visit!</p>
            <button onClick={reset} className="btn-primary inline-flex px-8 py-3.5" data-testid="waiver-sign-another">Sign Another Waiver</button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24">
      <Seo title="Liability Waiver | Keys Event Center" description="Sign the Keys Event Center liability waiver. Each parent or guardian signs for their child before play." />
      <div className="mx-auto max-w-3xl px-6">
        <SectionHeading eyebrow="Before You Play" title="Liability Waiver" subtitle="Each parent or guardian signs one waiver per participant. It only takes a minute." />

        <div className="card p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="grid place-items-center h-11 w-11 rounded-xl bg-[#2a2810] text-[#e0b52e] shrink-0"><ShieldCheck size={22} /></div>
            <h2 className="font-display text-xl font-bold text-[#f5f0ff]">{content?.title || "Acknowledgment of Risk & Limited Release of Liability"}</h2>
          </div>
          <div className="rounded-xl border border-[rgba(211,218,221,0.14)] bg-[#0e0b1a] p-4 max-h-64 overflow-y-auto text-sm text-[#b9bedd] whitespace-pre-line leading-relaxed mb-8" data-testid="waiver-body">
            {content?.body || "Loading…"}
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="wv-participant" className="text-sm text-[#a9bdbd] block mb-1.5">Participant / child's name</label>
              <input id="wv-participant" required aria-required="true" className={field} value={form.participant_name} onChange={(e) => set("participant_name", e.target.value)} placeholder="Full name of the person playing" data-testid="waiver-participant-name" />
            </div>
            <div>
              <label htmlFor="wv-dob" className="text-sm text-[#a9bdbd] block mb-1.5">Date of birth</label>
              <input id="wv-dob" type="date" className={field} value={form.participant_dob} onChange={(e) => set("participant_dob", e.target.value)} data-testid="waiver-dob" />
            </div>
            <div>
              <label htmlFor="wv-date-signed" className="text-sm text-[#a9bdbd] block mb-1.5">Date signed</label>
              <input id="wv-date-signed" type="date" readOnly className={`${field} opacity-80 cursor-not-allowed`} value={form.date_signed} data-testid="waiver-date-signed" />
            </div>
          </div>

          <label className="flex items-center gap-3 mt-5 cursor-pointer select-none" data-testid="waiver-minor-toggle">
            <input type="checkbox" className="h-5 w-5 accent-[#ff5147]" checked={form.is_minor} onChange={(e) => set("is_minor", e.target.checked)} />
            <span className="text-sm text-[#d9cbb0]">This participant is under 18 years old</span>
          </label>

          {form.is_minor && (
            <div className="mt-4">
              <label htmlFor="wv-guardian" className="text-sm text-[#a9bdbd] block mb-1.5">Parent / legal guardian name</label>
              <input id="wv-guardian" required aria-required="true" className={field} value={form.guardian_name} onChange={(e) => set("guardian_name", e.target.value)} placeholder="Parent or guardian full name" data-testid="waiver-guardian-name" />
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label htmlFor="wv-signer" className="text-sm text-[#a9bdbd] block mb-1.5">{form.is_minor ? "Guardian signing (full name)" : "Your full name"}</label>
              <input id="wv-signer" required aria-required="true" autoComplete="name" className={field} value={form.signer_name} onChange={(e) => set("signer_name", e.target.value)} placeholder="e.g. Jane A. Smith" data-testid="waiver-signer-name" />
            </div>
            <div>
              <label htmlFor="wv-email" className="text-sm text-[#a9bdbd] block mb-1.5">Email <span className="text-[#9aa0c0]">(optional)</span></label>
              <input id="wv-email" type="email" autoComplete="email" className={field} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" data-testid="waiver-email" />
            </div>
          </div>

          <p id="wv-sig-label" className="text-sm text-[#a9bdbd] block mb-1.5 mt-5">Signature <span className="text-[#9aa0c0]">(draw with your finger, stylus or mouse)</span></p>
          <div role="group" aria-labelledby="wv-sig-label" className="rounded-2xl border border-[rgba(211,218,221,0.14)] bg-white overflow-hidden relative" style={{ height: 180 }} data-testid="waiver-signature-pad">
            <SignatureCanvas ref={sigRef} penColor="#0a0a0a" canvasProps={{ className: "sig-canvas", role: "img", "aria-label": "Signature drawing area", style: { width: "100%", height: "180px", display: "block", touchAction: "none" } }} />
            <button type="button" onClick={() => sigRef.current?.clear()} className="absolute top-2 right-2 text-xs flex items-center gap-1 bg-[#ff5147] text-white px-2 py-1 rounded-lg" aria-label="Clear signature" data-testid="waiver-signature-clear"><Eraser size={12} aria-hidden="true" /> Clear</button>
          </div>

          <label className="flex items-start gap-3 mt-5 cursor-pointer select-none">
            <input type="checkbox" className="h-5 w-5 mt-0.5 accent-[#ff5147] shrink-0" checked={form.agreement_accepted} onChange={(e) => set("agreement_accepted", e.target.checked)} data-testid="waiver-agree" />
            <span className="text-sm text-[#a9bdbd]">I have read and agree to the waiver above{form.is_minor ? ", and as parent/legal guardian I give permission for the named minor to participate" : ""}. My electronic signature is legally binding.</span>
          </label>
          <label className="flex items-start gap-3 mt-3 cursor-pointer select-none">
            <input type="checkbox" className="h-5 w-5 mt-0.5 accent-[#ff5147] shrink-0" checked={form.esign_consent} onChange={(e) => set("esign_consent", e.target.checked)} data-testid="waiver-esign-consent" />
            <span className="text-sm text-[#a9bdbd]">{ESIGN_CONSENT_TEXT} <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-[#f6ecd4]">Privacy Policy</a></span>
          </label>

          <button onClick={submit} disabled={submitting} className="btn-primary w-full mt-6 py-4 text-base disabled:opacity-60" data-testid="waiver-submit">
            {submitting ? "Submitting…" : "Sign Waiver"}
          </button>
        </div>
      </div>
    </div>
  );
}
